import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraStatus = 'starting' | 'live' | 'denied';

/**
 * La cámara trasera, encendida mientras la pantalla esté montada.
 *
 * El apagado explícito importa: un `MediaStream` que nadie detiene deja la luz del teléfono
 * prendida y consume batería aunque la vista ya no esté.
 */
export function useCamera(active: boolean) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>('starting');

  useEffect(() => {
    if (!active) {
      return;
    }

    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }

        streamRef.current = stream;
        if (videoRef.current !== null) {
          videoRef.current.srcObject = stream;
        }
        setStatus('live');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('denied');
        }
      });

    return () => {
      cancelled = true;
      for (const track of streamRef.current?.getTracks() ?? []) {
        track.stop();
      }
      streamRef.current = null;
    };
  }, [active]);

  /** Congela el cuadro actual en un JPEG. El visor sigue vivo por si hay que repetir la toma. */
  const capture = useCallback(async (): Promise<File | null> => {
    const video = videoRef.current;
    if (video === null || video.videoWidth === 0) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (context === null) {
      return null;
    }

    context.drawImage(video, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });

    return blob === null ? null : new File([blob], 'captura.jpg', { type: 'image/jpeg' });
  }, []);

  return { videoRef, status, capture };
}
